import React from 'react';
import Editor, { type OnMount, type BeforeMount } from '@monaco-editor/react';
import clientLibraryTypesRaw from '../../generated/client-library-types.d.ts.txt?raw';
import type { ResolvedType } from '../../utils/resolveCodeBlockTypes';

const MIN_HEIGHT = 200;
const MAX_HEIGHT = 500;
const LINE_HEIGHT = 19;
const PADDING_LINES = 2;

const PREAMBLE_LINE_COUNT = 2;

/**
 * Build a typed preamble from the node's resolved input and output types.
 * The preamble uses contextual typing so `backend` gets ClientLibraryType
 * and destructured inputs/outputs get specific types with autocomplete.
 */
function buildPreamble(
  inputs?: ResolvedType[],
  outputs?: ResolvedType[]
): string {
  const inputsType = inputs && inputs.length > 0
    ? `{ ${inputs.map(( i ) => `${i.name}: ${i.tsType}` ).join( '; ' )} }`
    : 'Record<string, any>';

  const outputsType = outputs && outputs.length > 0
    ? `{ ${outputs.map(( o ) => `${o.name}: ${o.tsType}` ).join( '; ' )} }`
    : '{ output: any }';

  return `${[
    'import type { ClientLibraryType } from "./client-library";',
    `const __fn: (backend: ClientLibraryType, inputs: ${inputsType}) => Promise<${outputsType}> =`
  ].join( '\n' )}\n`;
}

export type CodeEditorProps = {
  value: string;
  /** Resolved input types connected to this CodeBlock node */
  resolvedInputs?: ResolvedType[];
  /** Resolved output types configured for this CodeBlock node */
  resolvedOutputs?: ResolvedType[];
  onChange: ( value: string ) => void;
  theme?: 'light' | 'dark';
};

/**
 * Monaco-based TypeScript code editor for CodeBlock nodes.
 * Loads client library type declarations for autocomplete/error checking.
 * Auto-resizes between MIN_HEIGHT and MAX_HEIGHT based on content.
 */
export function CodeEditor({ value, resolvedInputs, resolvedOutputs, onChange, theme = 'dark' }: CodeEditorProps ) {
  const [ height, setHeight ] = React.useState( MIN_HEIGHT );
  const editorRef = React.useRef<Parameters<OnMount>[0] | null>( null );
  const isUpdatingRef = React.useRef( false );
  const preamble = React.useMemo(() => buildPreamble( resolvedInputs, resolvedOutputs ), [ resolvedInputs, resolvedOutputs ]);
  const preambleRef = React.useRef( preamble );
  // preambleRef.current = preamble;
  const onChangeRef = React.useRef( onChange );
  // onChangeRef.current = onChange;

  const updateHeight = React.useCallback(( editor: Parameters<OnMount>[0]) => {
    const model = editor.getModel();
    if ( !model ) return;
    const totalLines = model.getLineCount();
    const visibleLines = totalLines - PREAMBLE_LINE_COUNT;
    const contentHeight = ( visibleLines + PADDING_LINES ) * LINE_HEIGHT;
    setHeight( Math.min( MAX_HEIGHT, Math.max( MIN_HEIGHT, contentHeight )));
  }, []);

  const handleBeforeMount: BeforeMount = React.useCallback(( monaco ) => {
    // Configure TypeScript compiler options — es2022 only, no DOM, no Node
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ES2022,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      lib: ['es2022'],
      allowNonTsExtensions: true,
      strict: true,
      noEmit: true,
      esModuleInterop: true,
      allowJs: false
    });

    monaco.languages.typescript.typescriptDefaults.setEagerModelSync( true );

    // Add client library types as an extra lib
    monaco.languages.typescript.typescriptDefaults.addExtraLib(
      clientLibraryTypesRaw,
      'file:///node_modules/@types/client-library/index.d.ts'
    );
  }, []);

  const hidePreamble = React.useCallback(( editor: Parameters<OnMount>[0]) => {
    try {
      const model = editor.getModel();
      if ( !model ) return;
      ( editor as any ).setHiddenAreas([{
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: PREAMBLE_LINE_COUNT,
        endColumn: model.getLineMaxColumn( PREAMBLE_LINE_COUNT )
      }]);
    } catch {
      // setHiddenAreas may not be available in all Monaco versions
    }
  }, []);

  const handleMount: OnMount = React.useCallback(( editor, monaco ) => {
    editorRef.current = editor;

    // Set model content with typed preamble
    const model = editor.getModel();
    if ( model ) {
      isUpdatingRef.current = true;
      model.setValue( preamble + value );
      isUpdatingRef.current = false;
      hidePreamble( editor );
    }

    updateHeight( editor );

    editor.onDidChangeModelContent(() => {
      if ( isUpdatingRef.current ) return;
      const fullValue = editor.getModel()?.getValue() ?? '';
      const currentPreamble = preambleRef.current;
      const rawValue = fullValue.startsWith( currentPreamble )
        ? fullValue.slice( currentPreamble.length )
        : fullValue;
      onChangeRef.current( rawValue );
      updateHeight( editor );
    });

    // Ctrl/Cmd+Shift+F: format document
    editor.addAction({
      id: 'format-document',
      label: 'Format Document',
      keybindings: [
        monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF
      ],
      run: ( ed ) => {
        ed.getAction( 'editor.action.formatDocument' )?.run();
      }
    });
  }, [ value, preamble, updateHeight, hidePreamble ]);

  // Sync external value changes without resetting cursor
  React.useEffect(() => {
    const editor = editorRef.current;
    if ( !editor ) return;
    const model = editor.getModel();
    if ( !model ) return;
    const currentFull = model.getValue();
    const expectedFull = preamble + value;
    if ( currentFull !== expectedFull ) {
      isUpdatingRef.current = true;
      model.setValue( expectedFull );
      isUpdatingRef.current = false;
      hidePreamble( editor );
    }
  }, [ value, preamble, hidePreamble ]);

  return (
    <div style={{ border: '1px solid var(--drawer-input-border, #444)', borderRadius: 4 }}>
      <div
        style={{
          padding: '4px 8px',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--drawer-label, #aaa)'
        }}
      >
        Code
      </div>
      <Editor
        height={height}
        defaultLanguage="typescript"
        defaultValue={preamble + value}
        theme={theme === 'dark' ? 'vs-dark' : 'light'}
        beforeMount={handleBeforeMount}
        onMount={handleMount}
        options={{
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          fontSize: 12,
          fontFamily: 'monospace',
          lineNumbers: ( lineNumber ) => {
            const adjusted = lineNumber - PREAMBLE_LINE_COUNT;
            return adjusted > 0 ? String( adjusted ) : '';
          },
          tabSize: 2,
          automaticLayout: true,
          wordWrap: 'on',
          folding: false,
          formatOnPaste: true,
          glyphMargin: false,
          overviewRulerBorder: false,
          renderLineHighlightOnlyWhenFocus: true,
          scrollbar: {
            vertical: 'auto',
            horizontal: 'auto',
            verticalScrollbarSize: 8,
            horizontalScrollbarSize: 8
          }
        }}
      />
    </div>
  );
}
